/**
 * EPC Contract commercial models — mirror of backend/src/modules/epc/commercial/commercial-models.ts.
 * Keep keys, labels and rules in sync (parity test in __tests__/epcCommercialModels.test.ts).
 */

export type CommercialModelKey =
  | 'LUMP_SUM'
  | 'REMEASUREMENT'
  | 'COST_PLUS'
  | 'UNIT_RATE'
  | 'HYBRID'
  | 'EPC_TURNKEY';

export type RecommendedContent = 'MILESTONES' | 'SOV' | 'SOV_AND_MILESTONES';
export type OverClaimPolicy = 'CAP_WARN' | 'ALLOW_INFO' | 'LINE_BASED';

export interface CommercialModelInfo {
  key: CommercialModelKey;
  label: string;
  aliases: string[];
  summary: string;
  meaning: string;
  contractValueMeaning: string;
  valueIsEstimate: boolean;
  recommendedContent: RecommendedContent;
  whatToEnter: string;
  ipcBasis: string;
  overClaimPolicy: OverClaimPolicy;
  keyControls: string[];
}

export const COMMERCIAL_MODELS: CommercialModelInfo[] = [
  {
    key: 'LUMP_SUM',
    label: 'Lump Sum',
    aliases: ['LumpSum', 'LS', 'Fixed Price'],
    summary: 'Fixed price for a defined scope; paid against milestones or progress.',
    meaning:
      'The contractor delivers the defined scope for a fixed price and carries the quantity risk. The price changes only through approved Variations.',
    contractValueMeaning: 'Fixed contract sum.',
    valueIsEstimate: false,
    recommendedContent: 'MILESTONES',
    whatToEnter:
      'Payment milestones (percent or amount, totalling 100% / the contract value). Optionally a Schedule of Values price breakdown.',
    ipcBasis:
      'Each IPC claims achieved milestones or a progress percentage of each line. Cumulative claims should not exceed 100% of a line.',
    overClaimPolicy: 'CAP_WARN',
    keyControls: [
      'Claims above 100% of a milestone or line are flagged.',
      'Scope or price changes need an approved Variation.',
    ],
  },
  {
    key: 'REMEASUREMENT',
    label: 'Remeasurement',
    aliases: ['Remeasured', 'RemeasuredBOQ', 'Re-measurement', 'Measure and Value'],
    summary: 'Bill of quantities at fixed rates; actual quantities are measured and paid.',
    meaning:
      'Rates are fixed but quantities in the bill are estimates. Work is measured jointly (usually monthly) and paid at the contract rates, so the final value follows the measured quantities.',
    contractValueMeaning: 'Estimated contract sum (quantities are remeasured).',
    valueIsEstimate: true,
    recommendedContent: 'SOV',
    whatToEnter: 'A Schedule of Values with item code, unit, estimated quantity and rate for every measured item.',
    ipcBasis:
      'Each IPC claims the measured quantity for the period times the contract rate; previous and cumulative quantities are carried forward.',
    overClaimPolicy: 'LINE_BASED',
    keyControls: [
      'Quantities above the bill quantity are allowed but noted (remeasurement / variation evidence needed).',
      'Lump-sum items (unit "sum", "LS" or "lot", e.g. preliminaries) above 100% are flagged.',
      'Keep joint measurement records for every IPC.',
    ],
  },
  {
    key: 'COST_PLUS',
    label: 'Cost Plus',
    aliases: ['CostPlus', 'Cost Reimbursable', 'Reimbursable'],
    summary: 'Actual cost reimbursed plus an agreed fee.',
    meaning:
      'The client reimburses the actual, auditable cost of the work (labour, materials, subcontracts) plus an agreed fee (percentage or fixed). The client carries the cost risk; open-book records are essential.',
    contractValueMeaning: 'Budget / estimate of cost plus fee (not a fixed price).',
    valueIsEstimate: true,
    recommendedContent: 'SOV',
    whatToEnter:
      'A Schedule of Values with reimbursable cost lines (Prime Cost, Day-work) and a separate fee line; record the fee % in the commercial terms.',
    ipcBasis:
      'Each IPC claims the actual cost incurred in the period on each cost line plus the fee on that cost. The fee line is entered manually.',
    overClaimPolicy: 'ALLOW_INFO',
    keyControls: [
      'Cost over-runs above the budget line are allowed but noted.',
      'A fee line and fee % should be present.',
      'Support every claim with cost statements (open book).',
    ],
  },
  {
    key: 'UNIT_RATE',
    label: 'Unit Rate',
    aliases: ['UnitRate', 'Schedule of Rates', 'Call-off', 'Framework'],
    summary: 'Fixed unit rates; quantities called off as needed.',
    meaning:
      'A schedule of fixed rates (per unit, per job, per day) used for work ordered as needed, typically under a framework or call-off. Quantities are indicative only.',
    contractValueMeaning: 'Estimated contract sum (indicative quantities x rates).',
    valueIsEstimate: true,
    recommendedContent: 'SOV',
    whatToEnter: 'A Schedule of Values listing each rate item with its unit, indicative quantity and fixed rate.',
    ipcBasis: 'Each IPC claims the quantity called off and completed in the period times the fixed rate.',
    overClaimPolicy: 'LINE_BASED',
    keyControls: [
      'Quantities above the indicative quantity are allowed but noted.',
      'Lump-sum items (unit "sum", "LS" or "lot") above 100% are flagged.',
      'Each claim should reference the client call-off / work order.',
    ],
  },
  {
    key: 'HYBRID',
    label: 'Hybrid',
    aliases: ['Mixed'],
    summary: 'Part lump sum, part remeasured or reimbursable.',
    meaning:
      'Combines models in one contract, for example lump-sum design and fabrication with remeasured installation and provisional sums.',
    contractValueMeaning: 'Contract sum: fixed for lump-sum items, estimated for measured items.',
    valueIsEstimate: false,
    recommendedContent: 'SOV',
    whatToEnter:
      'A Schedule of Values where lump-sum items use the unit "sum" / "LS" / "lot" and measured items carry unit, quantity and rate. Milestones are optional.',
    ipcBasis:
      'Lump-sum items are claimed by progress percentage (capped at 100%); measured items by measured quantity times rate.',
    overClaimPolicy: 'LINE_BASED',
    keyControls: [
      'Lump-sum items above 100% are flagged; measured items above the bill quantity are noted.',
      'Mark lump-sum items clearly with the unit "sum", "LS" or "lot".',
    ],
  },
  {
    key: 'EPC_TURNKEY',
    label: 'EPC Turnkey',
    aliases: ['EPC', 'Turnkey', 'EPCTurnkey', 'Design and Build'],
    summary: 'Single-point responsibility for engineering, procurement and construction at a fixed price.',
    meaning:
      'The contractor takes single-point responsibility for engineering, procurement, construction and commissioning, handing over a working facility for a fixed price.',
    contractValueMeaning: 'Fixed contract sum.',
    valueIsEstimate: false,
    recommendedContent: 'SOV_AND_MILESTONES',
    whatToEnter:
      'Payment milestones (totalling 100%) plus a Schedule of Values price breakdown (engineering, procurement, construction, commissioning) linked to the milestones.',
    ipcBasis:
      'Each IPC claims achieved milestones or progress of the price-breakdown lines. Cumulative claims should not exceed 100% of a line.',
    overClaimPolicy: 'CAP_WARN',
    keyControls: [
      'Claims above 100% of a milestone or line are flagged.',
      'Performance guarantees and liquidated damages usually apply; scope changes need a Variation.',
    ],
  },
];

const squash = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');

const BY_ALIAS = new Map<string, CommercialModelInfo>();
for (const m of COMMERCIAL_MODELS) {
  for (const a of [m.label, m.key, ...m.aliases]) BY_ALIAS.set(squash(a), m);
}

export function commercialModelInfo(value: unknown): CommercialModelInfo | null {
  const s = value == null ? '' : String(value).trim();
  return s ? BY_ALIAS.get(squash(s)) ?? null : null;
}

export function normalizeCommercialModel(value: unknown): string | null {
  const s = value == null ? '' : String(value).trim();
  if (!s) return null;
  return commercialModelInfo(s)?.label ?? s;
}

export function isLumpSumUnit(unit: unknown): boolean {
  return /^(sum|ls|l\.s\.?|lot|lump ?sum)$/i.test(String(unit ?? '').trim());
}

/** CAP_WARN lines may not exceed 100% in the IPC form; ALLOW_INFO lines may be over-measured (flagged). */
export function lineOverClaimPolicy(model: unknown, unit: unknown): 'CAP_WARN' | 'ALLOW_INFO' {
  const policy = commercialModelInfo(model)?.overClaimPolicy ?? 'CAP_WARN';
  if (policy === 'LINE_BASED') return isLumpSumUnit(unit) ? 'CAP_WARN' : 'ALLOW_INFO';
  return policy;
}

export type OverClaimSeverity = 'OVER_CLAIM' | 'OVER_MEASURE';

/** Same rule as backend ipc-over-claims.ts for one line. */
export function lineOverClaim(
  model: unknown,
  line: {
    unit?: string | null;
    lineAmount: number;
    previousCertified: number;
    appliedAmount: number;
    contractQty?: number | null;
    cumulativeQty?: number | null;
  }
): OverClaimSeverity | null {
  const amount = Number(line.lineAmount) || 0;
  if (amount <= 0) return null;
  const cumulative = Math.round(((Number(line.previousCertified) || 0) + (Number(line.appliedAmount) || 0)) * 100) / 100;
  const qtyOver =
    line.contractQty != null && line.cumulativeQty != null && Number(line.contractQty) > 0
      ? Number(line.cumulativeQty) - Number(line.contractQty) > 0.0001
      : false;
  if (cumulative - amount <= 0.01 && !qtyOver) return null;
  return lineOverClaimPolicy(model, line.unit) === 'CAP_WARN' ? 'OVER_CLAIM' : 'OVER_MEASURE';
}

/** Workspace tab to open first for the model's recommended content. */
export function recommendedContentTab(value: unknown): 'sov' | 'milestones' {
  const info = commercialModelInfo(value);
  return info?.recommendedContent === 'MILESTONES' || info?.recommendedContent === 'SOV_AND_MILESTONES'
    ? 'milestones'
    : 'sov';
}

export const COMMERCIAL_MODEL_OPTIONS = COMMERCIAL_MODELS.map((m) => ({ value: m.label, label: m.label }));

/** Contract print internal copy; must match the server's epc-contract report definition. */
export const EPC_CONTRACT_INTERNAL_COPY_PERMISSIONS = ['epc.contract.manage', 'epc.contract_admin.manage', 'epc.admin'];
